import React from "react";
import {
  DocLayout,
  DocBreadcrumb,
  DocTitle,
  DocSection,
  DocCard,
  DocAlert,
  DocStepIndicator,
  DocCodeBlock,
  DocNavFooter,
  DocLink,
} from "@/components/docs";
import { docsColors } from "@/lib/docs-theme";

export default function DiscordIntegrationPage() {
  const breadcrumbs = [
    { label: "Documentation", href: "/docs" },
    { label: "Notification Methods", href: "/docs/notifications" },
    { label: "Discord" },
  ];

  return (
    <DocLayout
      title="Discord Integration - Uptime Monitor Documentation"
      description="Send monitor alerts to Discord servers. Real-time notifications with rich embeds for your community and team channels."
      canonical="https://uptime-monitor.io/docs/notifications/discord"
    >
      <DocBreadcrumb items={breadcrumbs} />

      <DocTitle
        title="Discord Integration"
        description="Send monitor alerts to Discord channels using webhooks. Get notified when monitors go down or recover."
      />

      {/* Overview */}
      <DocSection>
        <DocAlert variant="info" className="p-6">
          <h2 className={`text-xl font-semibold ${docsColors.text.heading} mb-3`}>How It Works</h2>
          <p className={`${docsColors.text.body} mb-4`}>
            Uptime Monitor sends alerts to Discord using webhooks. When a monitor goes down or
            recovers, a formatted message is posted to your configured channel.
          </p>
          <ul className={`space-y-2 ${docsColors.text.body}`}>
            <li className="flex items-start">
              <span className={`${docsColors.text.muted} mr-2`}>•</span>
              Monitor name appears as a clickable link to the dashboard
            </li>
            <li className="flex items-start">
              <span className={`${docsColors.text.muted} mr-2`}>•</span>
              Down alerts show the error reason
            </li>
            <li className="flex items-start">
              <span className={`${docsColors.text.muted} mr-2`}>•</span>
              Recovery alerts include total downtime
            </li>
          </ul>
        </DocAlert>
      </DocSection>

      {/* Webhook Setup */}
      <DocSection title="Setup Instructions">
        <div className="mb-8">
          <h3 className={`text-xl font-semibold ${docsColors.text.heading} mb-4`}>
            Step 1: Create Webhook in Discord
          </h3>
          <DocCard>
            <ol className="space-y-4">
              <li>
                <div className="flex items-start">
                  <DocStepIndicator number={1} />
                  <div>
                    <p className={`${docsColors.text.body}`}>
                      Open your Discord server and go to the channel where you want alerts
                    </p>
                  </div>
                </div>
              </li>

              <li>
                <div className="flex items-start">
                  <DocStepIndicator number={2} />
                  <div>
                    <p className={`${docsColors.text.body}`}>
                      Click the gear icon next to the channel name to open{" "}
                      <strong className={docsColors.text.heading}>Channel Settings</strong>
                    </p>
                  </div>
                </div>
              </li>

              <li>
                <div className="flex items-start">
                  <DocStepIndicator number={3} />
                  <div>
                    <p className={`${docsColors.text.body}`}>
                      Navigate to{" "}
                      <strong className={docsColors.text.heading}>Integrations → Webhooks</strong>
                    </p>
                  </div>
                </div>
              </li>

              <li>
                <div className="flex items-start">
                  <DocStepIndicator number={4} />
                  <div>
                    <p className={`${docsColors.text.body}`}>
                      Click <strong className={docsColors.text.heading}>Create Webhook</strong> or{" "}
                      <strong className={docsColors.text.heading}>New Webhook</strong>
                    </p>
                  </div>
                </div>
              </li>

              <li>
                <div className="flex items-start">
                  <DocStepIndicator number={5} />
                  <div>
                    <p className={`${docsColors.text.body}`}>
                      Give it a name like &quot;Uptime Monitor&quot; and optionally upload an avatar
                    </p>
                  </div>
                </div>
              </li>

              <li>
                <div className="flex items-start">
                  <DocStepIndicator number={6} />
                  <div>
                    <p className={`${docsColors.text.body}`}>
                      Click <strong className={docsColors.text.heading}>Copy Webhook URL</strong>{" "}
                      and save it securely
                    </p>
                  </div>
                </div>
              </li>
            </ol>

            <DocAlert variant="warning" className="mt-6 p-4">
              <p className={`text-sm ${docsColors.text.body}`}>
                <strong className={docsColors.text.heading}>Security Note:</strong> Keep your
                webhook URL private. Anyone with this URL can send messages to your channel.
              </p>
            </DocAlert>
          </DocCard>
        </div>

        <div className="mb-8">
          <h3 className={`text-xl font-semibold ${docsColors.text.heading} mb-4`}>
            Step 2: Configure in Uptime Monitor
          </h3>
          <DocCard>
            <ol className="space-y-4">
              <li>
                <div className="flex items-start">
                  <DocStepIndicator number={1} />
                  <div>
                    <p className={`${docsColors.text.body}`}>
                      In Uptime Monitor, go to{" "}
                      <strong className={docsColors.text.heading}>Settings → Contacts</strong>
                    </p>
                  </div>
                </div>
              </li>

              <li>
                <div className="flex items-start">
                  <DocStepIndicator number={2} />
                  <div>
                    <p className={`${docsColors.text.body}`}>
                      Click <strong className={docsColors.text.heading}>Add Contact</strong> and
                      select <strong className={docsColors.text.heading}>Discord</strong>
                    </p>
                  </div>
                </div>
              </li>

              <li>
                <div className="flex items-start">
                  <DocStepIndicator number={3} />
                  <div>
                    <p className={`${docsColors.text.body}`}>
                      Enter a name (e.g., &quot;Production Alerts&quot;) and paste your webhook URL
                    </p>
                  </div>
                </div>
              </li>

              <li>
                <div className="flex items-start">
                  <DocStepIndicator number={4} />
                  <div>
                    <p className={`${docsColors.text.body}`}>Save the contact</p>
                  </div>
                </div>
              </li>

              <li>
                <div className="flex items-start">
                  <DocStepIndicator number={5} />
                  <div>
                    <p className={`${docsColors.text.body}`}>
                      Add the Discord contact to your monitors
                    </p>
                  </div>
                </div>
              </li>
            </ol>
          </DocCard>
        </div>
      </DocSection>

      {/* Message Format */}
      <DocSection title="Discord Message Format">
        <DocCard>
          <p className={`${docsColors.text.body} mb-4`}>
            Messages are formatted with the monitor name as a hyperlink:
          </p>

          <div className="space-y-6">
            <div>
              <h3 className={`font-semibold ${docsColors.text.heading} mb-3`}>Down Alert</h3>
              <DocCodeBlock className="border border-slate-700">
                <p className={`${docsColors.text.body}`}>
                  👻 <DocLink href="#">Production API</DocLink> is DOWN: Connection timeout after
                  10000ms.
                </p>
              </DocCodeBlock>
            </div>

            <div>
              <h3 className={`font-semibold ${docsColors.text.heading} mb-3`}>Recovery Alert</h3>
              <DocCodeBlock className="border border-slate-700">
                <p className={`${docsColors.text.body}`}>
                  👻 <DocLink href="#">Production API</DocLink> is UP! It was down for 5 minutes.
                </p>
              </DocCodeBlock>
            </div>
          </div>

          <DocAlert variant="info" className="mt-4 p-3">
            <p className={`text-sm ${docsColors.text.body}`}>
              <strong className={docsColors.text.heading}>Note:</strong> The monitor name links
              directly to the monitor details page in your dashboard.
            </p>
          </DocAlert>
        </DocCard>
      </DocSection>

      {/* Best Practices */}
      <DocSection title="Best Practices">
        <DocCard>
          <ul className="space-y-3">
            <li className="flex items-start">
              <span className="text-blue-600 dark:text-blue-400 mr-2 mt-0.5">✓</span>
              <div>
                <strong className={docsColors.text.heading}>Use dedicated alert channels</strong>
                <p className={`${docsColors.text.body} text-sm mt-1`}>
                  Create separate channels for different environments or severity levels
                </p>
              </div>
            </li>
            <li className="flex items-start">
              <span className="text-blue-600 dark:text-blue-400 mr-2 mt-0.5">✓</span>
              <div>
                <strong className={docsColors.text.heading}>
                  Multiple contacts for redundancy
                </strong>
                <p className={`${docsColors.text.body} text-sm mt-1`}>
                  Create multiple Discord contacts for different channels and assign to monitors
                </p>
              </div>
            </li>
            <li className="flex items-start">
              <span className="text-blue-600 dark:text-blue-400 mr-2 mt-0.5">✓</span>
              <div>
                <strong className={docsColors.text.heading}>Test webhook connectivity</strong>
                <p className={`${docsColors.text.body} text-sm mt-1`}>
                  Verify webhooks are working by pausing and resuming a monitor
                </p>
              </div>
            </li>
            <li className="flex items-start">
              <span className="text-blue-600 dark:text-blue-400 mr-2 mt-0.5">✓</span>
              <div>
                <strong className={docsColors.text.heading}>Configure channel notifications</strong>
                <p className={`${docsColors.text.body} text-sm mt-1`}>
                  Set up Discord channel notification preferences to avoid missing alerts
                </p>
              </div>
            </li>
          </ul>
        </DocCard>
      </DocSection>

      {/* Troubleshooting */}
      <DocSection title="Troubleshooting">
        <div className="space-y-4">
          <DocAlert variant="warning" className="p-6">
            <h3 className={`text-lg font-semibold ${docsColors.text.heading} mb-3`}>
              Common Issues
            </h3>
            <div className="space-y-4">
              <div>
                <h4 className={`font-semibold ${docsColors.text.heading} mb-2`}>
                  Messages not appearing
                </h4>
                <ul className={`space-y-1 text-sm ${docsColors.text.body} ml-4`}>
                  <li>• Verify webhook URL is correct and complete</li>
                  <li>• Check channel exists and webhook hasn&apos;t been deleted</li>
                  <li>• Ensure Discord contact is assigned to monitors</li>
                  <li>• Verify webhook is not disabled in Discord</li>
                </ul>
              </div>

              <div>
                <h4 className={`font-semibold ${docsColors.text.heading} mb-2`}>Webhook errors</h4>
                <ul className={`space-y-1 text-sm ${docsColors.text.body} ml-4`}>
                  <li>• Regenerate webhook URL in Discord</li>
                  <li>• Check for server restrictions</li>
                  <li>• Ensure webhook hasn&apos;t been deleted</li>
                  <li>• Verify network connectivity</li>
                </ul>
              </div>
            </div>
          </DocAlert>
        </div>
      </DocSection>

      <DocNavFooter
        prev={{ href: "/docs/notifications/slack", text: "Slack" }}
        next={{ href: "/docs/notifications/ms-teams", text: "Microsoft Teams" }}
      />
    </DocLayout>
  );
}
